import { AspectRatio, Box, Text, useDisclosure } from '@chakra-ui/react';
import type { RichTextResolverProp } from '@time-line/components/common';
import { Drawer } from '@time-line/components/common';
import type { Link } from '@time-line/components/types';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import type { PartnerPerk } from 'types/cms/blocks/PartnerPerksCarousel';

import { PartnerPerkDrawer } from './PartnerPerkDrawer';

interface PartnerPerkCardProps extends PartnerPerk {
  cta: Link;
  RichTextResolver?: RichTextResolverProp;
}

export function PartnerPerkCard({
  image,
  title,
  description,
  shopUrl,
  cta,
  RichTextResolver,
}: PartnerPerkCardProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const imageProps = useImage(image, { fill: true });
  const isClickable = !!description?.length || !!shopUrl;

  const content = (
    <>
      {imageProps && <Image {...imageProps} style={{ objectFit: 'cover' }} />}
      {title && (
        <Box
          position="absolute"
          insetX={0}
          bottom={0}
          pt="2xl"
          pb="sm"
          px="sm"
          bgGradient="linear(to-b, transparent, blackAlpha.400)"
        >
          <Text
            as="p"
            size="h5"
            color="gray.50"
            textAlign="center"
            sx={{ textWrap: 'pretty' }}
          >
            {title}
          </Text>
        </Box>
      )}
    </>
  );

  return (
    <>
      <AspectRatio ratio={4 / 5}>
        {isClickable ? (
          <Box
            as="button"
            type="button"
            onClick={onOpen}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={title ? `View more: ${title}` : 'View more'}
            overflow="hidden"
            borderRadius="base"
            textAlign="left"
          >
            {content}
          </Box>
        ) : (
          <Box overflow="hidden" borderRadius="base" textAlign="left">
            {content}
          </Box>
        )}
      </AspectRatio>
      {isClickable && (
        <Drawer
          isOpen={isOpen}
          onClose={onClose}
          maxWidth="lg"
          bodyPb={0}
          width={{ base: '100%', md: '72.5%', xl: '50%' }}
        >
          <PartnerPerkDrawer
            image={image}
            title={title}
            description={description}
            shopUrl={shopUrl}
            cta={cta}
            RichTextResolver={RichTextResolver}
          />
        </Drawer>
      )}
    </>
  );
}
